import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  listCategories,
  listStreams,
  listTags,
  readStream,
  type CategoryInfo,
  type StoryCard,
  type Stream,
  type StreamKey,
  type TagInfo,
} from '../api/discover';
import { useAuth } from '../auth/useAuth';
import { StoryCardView } from '../components/discover/StoryCardView';
import { AppShell } from '../components/layout/AppShell';
import { Button, EmptyState, ErrorState, LoadingState } from '../components/ui';
import './DiscoverPage.css';

type State = 'loading' | 'ready' | 'error';

const DEFAULT_STREAM: StreamKey = 'feed';

/**
 * `/discover` — what other people have published.
 *
 * The streams are asked for rather than hardcoded, so what this page offers is
 * whatever the backend says it can serve. Every stream is the same list read a
 * different way, which is why they are tabs over one result rather than five
 * pages.
 */
export function DiscoverPage() {
  const { account } = useAuth();
  const [params, setParams] = useSearchParams();

  const stream = (params.get('stream') as StreamKey | null) ?? DEFAULT_STREAM;
  const category = params.get('category') ?? '';
  const tag = params.get('tag') ?? '';

  const [state, setState] = useState<State>('loading');
  const [streams, setStreams] = useState<Stream[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [tags, setTags] = useState<TagInfo[]>([]);
  const [cards, setCards] = useState<StoryCard[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [offered, shelves, words, answer] = await Promise.all([
        listStreams(),
        listCategories(),
        listTags(),
        readStream(stream, { category, tag }),
      ]);
      setStreams(offered);
      setCategories(shelves);
      setTags(words);
      setCards(answer.results);
      setPage(answer.page);
      setPages(answer.total_pages);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [stream, category, tag]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const answer = await readStream(stream, { category, tag, page: page + 1 });
      setCards((current) => [...current, ...answer.results]);
      setPage(answer.page);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setLoadingMore(false);
    }
  };

  /** One place changes the address; the address is what the page reads. */
  const choose = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next);
  };

  const chosen = streams.find((entry) => entry.key === stream);

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Discover</h1>
        <p>{chosen?.description ?? 'What other people have published.'}</p>
      </div>
    </div>
  );

  if (state === 'error') {
    return (
      <AppShell header={header}>
        <ErrorState
          title="We could not load this"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell header={header}>
      <nav className="sc-discover__streams" aria-label="Ways of reading">
        {streams.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`sc-discover__stream${entry.key === stream ? ' is-chosen' : ''}`}
            aria-pressed={entry.key === stream}
            onClick={() => choose({ stream: entry.key })}
          >
            {entry.name}
          </button>
        ))}
      </nav>

      <div className="sc-discover__filters">
        <label className="sc-discover__field">
          <span>Category</span>
          <select value={category} onChange={(event) => choose({ category: event.target.value })}>
            <option value="">Everything</option>
            {categories.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        {tag ? (
          <Button size="sm" variant="secondary" onClick={() => choose({ tag: '' })}>
            Clear #{tag}
          </Button>
        ) : (
          <div className="sc-discover__tags">
            {tags.slice(0, 12).map((entry) => (
              <button
                key={entry.slug}
                type="button"
                className="sc-discover__tag"
                onClick={() => choose({ tag: entry.slug })}
              >
                #{entry.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {state === 'loading' ? (
        <LoadingState label="Looking…" />
      ) : cards.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          description={
            stream === 'following'
              ? account
                ? 'Follow someone and their stories will appear here.'
                : 'Sign in and follow someone to read them here.'
              : 'When people publish stories, they show up here.'
          }
        />
      ) : (
        <div className="sc-discover__cards">
          {cards.map((card) => (
            <StoryCardView
              key={card.id}
              card={card}
              onTag={(slug) => choose({ tag: slug })}
              onCategory={(slug) => choose({ category: slug })}
            />
          ))}
        </div>
      )}

      {state === 'ready' && page < pages && (
        <div className="sc-discover__more">
          <Button variant="secondary" isLoading={loadingMore} onClick={() => void more()}>
            Read more
          </Button>
        </div>
      )}
    </AppShell>
  );
}
